import axios from 'axios';

/** When the browser cannot reach the API or an upstream restart returns a gateway error. */
export const CONNECTION_ISSUE_MESSAGE =
  'There appears to be a connection issue. Please refresh the page and try again in about 30 seconds.';

type ApiValidationDetail = {
  field?: string;
  message?: string;
};

type ApiErrorPayload = {
  error?: string;
  message?: string;
  details?: ApiValidationDetail[];
};

/** Prefer server-provided validation/API errors; use {@link CONNECTION_ISSUE_MESSAGE} for unreachable or warming-up backends. */
export function getFriendlyApiError(error: unknown, fallback: string): string {
  if (!axios.isAxiosError(error)) {
    return fallback;
  }

  if (!error.response) {
    return CONNECTION_ISSUE_MESSAGE;
  }

  const status = error.response.status;
  if (status === 502 || status === 503 || status === 504) {
    return CONNECTION_ISSUE_MESSAGE;
  }

  const data = error.response.data as ApiErrorPayload | undefined;
  if (Array.isArray(data?.details) && data.details.length > 0) {
    return data.details
      .map((detail) => detail.message)
      .filter((message): message is string => Boolean(message))
      .join(' ');
  }

  if (data?.error && typeof data.error === 'string') {
    return data.error;
  }
  if (data?.message && typeof data.message === 'string') {
    return data.message;
  }

  return fallback;
}
